import { Injectable } from '@angular/core';
import { StorageMap } from '@ngx-pwa/local-storage';
import defaultsDeep from 'lodash-es/defaultsDeep';
import omit from 'lodash-es/omit';
import { Observable, Subject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { GaSessionConfig, GaSessionConfigService } from '../../session/index';
import { GaApplicationsConfig } from './../../applications/models/ApplicationConfig';
import { GaApplicationsConfigService } from './../../applications/services/applications.config.service';
import { GaConfig } from './../models/Config';
import { GaI18nConfig } from './../models/I18nConfig';
import { GaShellConfig } from './../models/ShellConfig';

@Injectable()
export class GaConfigService {
    #changes = new Subject();
    #i18nConfig = new GaI18nConfig();
    #shellConfig = new GaShellConfig();
    [key: string]: any;

    constructor(
        private applicationsConfigService: GaApplicationsConfigService,
        private sessionConfigService: GaSessionConfigService,
        private storage: StorageMap
    ) { }

    get applications(): GaApplicationsConfig { return this.applicationsConfigService.config; }
    get i18n(): GaI18nConfig { return this.#i18nConfig; }
    get session(): GaSessionConfig { return this.sessionConfigService.config; }
    get shell(): GaShellConfig { return this.#shellConfig; }
    get config(): GaConfig {
        return {
            i18n: this.#i18nConfig,
            session: this.sessionConfigService.config,
            shell: this.#shellConfig,
            ...omit(this, ['#i18nConfig', '#shellConfig'])
        } as GaConfig;
    }
    get config$(): Observable<GaConfig> { return this.#changes.pipe(startWith(null), map(() => this.config)); }

    get language(): string {
        let lsLanguage;
        this.storage.get('language').subscribe(data => lsLanguage = data);
        return lsLanguage ?? this.i18n.application.defaultLanguage;
    }

    private addShellConfig(config: GaShellConfig): void {
        this.#shellConfig.shadows.left = config?.shadows?.left ?? this.#shellConfig.shadows.left;
        this.#shellConfig.shadows.top = config?.shadows?.left ?? this.#shellConfig.shadows.top;
        this.#shellConfig.shadows.right = config?.shadows?.left ?? this.#shellConfig.shadows.right;
        const restConfig = omit(config, ['i18n', 'session', 'shell']);
        for (const key of Object.keys(restConfig)) {
            if (restConfig.hasOwnProperty(key)) {
                if (this.hasOwnProperty(key)) {
                    if (Array.isArray(this[key])) {
                        (this[key] as any[]).push(...restConfig[key]);
                    } else if (typeof restConfig[key] === 'object') {
                        this[key] = { ...this[key], ...restConfig[key] };
                    } else {
                        this[key] = restConfig[key];
                    }
                } else {
                    Object.defineProperty(this, key, { value: restConfig[key] });
                }
            }
        }
    }

    addConfig(config: GaConfig): void {
        this.applicationsConfigService.addConfig(config?.applications);
        this.#i18nConfig = defaultsDeep(config?.i18n ?? {}, this.#i18nConfig);
        this.sessionConfigService.addConfig(config?.session);
        this.addShellConfig(config?.shell);
        this.#changes.next();
    }
}
